import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetWorkspaceArchived } from '../../../entities/workspace';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { Button } from '../../../shared/ui/button';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { toast } from '../../../shared/ui/sonner';

/** Archive (confirmed) or unarchive (direct) a workspace: `workspace.manage`. Nothing is ever deleted (Q55). */
export function ArchiveWorkspaceButton({
  workspace,
}: {
  workspace: { id: string; name: string; archived_at: string | null };
}) {
  const { t } = useTranslation('workspace');
  const setArchived = useSetWorkspaceArchived(workspace.id);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (workspace.archived_at !== null)
    return (
      <div className="grid gap-2">
        <div>
          <Button
            disabled={setArchived.isPending}
            onClick={async () => {
              setError(null);
              try {
                await setArchived.mutateAsync(false);
                toast.success(t('settings.unarchived'));
              } catch (e) {
                setError(identityErrorMessage(e));
              }
            }}
          >
            {t('settings.unarchive')}
          </Button>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    );

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {t('settings.archive')}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('settings.archiveConfirmTitle', { name: workspace.name })}
        description={t('settings.archiveConfirmBody')}
        confirmLabel={t('settings.archiveConfirm')}
        cancelLabel={t('settings.archiveCancel')}
        variant="primary"
        errorMessage={identityErrorMessage}
        onConfirm={async () => {
          await setArchived.mutateAsync(true);
          toast.success(t('settings.archived'));
        }}
      />
    </>
  );
}
